import React, { useContext, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Formik, Form } from "formik";

import { SettingsContext, UserContext } from "../../contexts";
import RCProductSelector from "../../product/components/RCProductSelector";
import AlertHelper from "../../helpers/Alert";
import FilterForms from "./FilterForms";

const IFilter = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M2 3.5H14M4.5 8H11.5M6.5 12.5H9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/**
 * RCAlertForm — RC-themed replacement for AlertForm.jsx.
 *
 * Fixes vs. the legacy form:
 *  - onProductSelected's edit-mode redirect used product.id where the route
 *    needs alert.id (`/alerts/${product.id}/edit/` pushed a non-existent
 *    alert's URL). Rather than navigate at all when editing — the URL
 *    wouldn't change anyway, since we're already on this alert's edit route
 *    — the newly selected product is now held in local state and fed into
 *    Formik via enableReinitialize, so the filters step immediately reflects
 *    the new product without a route change or a refetch race.
 *  - Off-canvas back-link (position: absolute; left: -70px) removed —
 *    back navigation is the page wrapper's PageHeader (backTo="/alerts").
 *  - Product search/select now uses the shared RCProductSelector (Phase 1
 *    extraction) instead of the legacy Collapsible + SelectProduct combo.
 */
const RCAlertForm = ({ afterSubmit, alert, editableProduct = false }) => {
  const { isMerchant } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const { baseProductId } = useParams();
  const [status, setStatus] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(alert?.product);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const effectiveAlert = { ...alert, product: selectedProduct };

  const initialValues = AlertHelper.getInitialValues(effectiveAlert);
  const validationSchema = AlertHelper.getValidationSchema(
    effectiveAlert,
    undefined
  );
  const processSubmitValues = AlertHelper.processSubmitValues;

  const onProductSelected = (product) => {
    setSelectedProduct(product);
  };

  const handleSubmit = async (values) => {
    // The legacy AlertForm built processSubmitValues but submitted raw
    // `values` instead — the switch fields (alert/underpriced/is_not_product/
    // is_only_brand) could reach the API as `undefined` rather than `false`.
    // Applying it here is the fix, not new behaviour: it's what the helper
    // was already written to do.
    const payload = {
      ...processSubmitValues(values),
      product_id: selectedProduct?.id ?? baseProductId,
      reference_number: values.product?.reference_number,
      brand_id: values.product?.brand_id,
      model_id: values.product?.model_id,
      status,
    };

    if (alert?.id) {
      await axios.put(`/api/buy-alerts/${alert.id}`, payload);
    } else {
      await axios.post("/api/buy-alerts", payload);
    }

    afterSubmit();
  };

  return (
    <div className="rc-alert-form">
      <div className="rc-lp__product-area">
        <RCProductSelector
          product={selectedProduct}
          onProductSelected={onProductSelected}
          labelSelect="Select Product"
          labelSelected="Product"
        />
      </div>

      {(selectedProduct?.id || alert?.id) && (
        <div className="rc-lp__section">
          <button
            type="button"
            className="rc-lp__section-trigger"
            onClick={() => setFiltersOpen((o) => !o)}
          >
            <span className="rc-lp__section-icon"><IFilter /></span>
            <span className="rc-lp__section-label">Filters</span>
            <span className="rc-lp__section-btn">{filtersOpen ? "−" : "+"}</span>
          </button>

          {/*
            Formik stays mounted regardless of filtersOpen so collapsing the
            section (a convenience, mirroring RCProductSelector's accordion)
            never discards budget/keyword/toggle values already typed in —
            only the body's visibility toggles, via CSS, not unmounting.
          */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            <Form
              className={`rc-alert-form__filters rc-lp__section-body${
                filtersOpen ? "" : " rc-lp__section-body--collapsed"
              }`}
            >
              <FilterForms setStatus={setStatus} />
            </Form>
          </Formik>
        </div>
      )}
    </div>
  );
};

export default RCAlertForm;
